import { List, Typography } from 'antd';
import { ListItemProps } from 'antd/lib/list';
import { ProspectDrawerTrackingParams } from '@copilot/common/components/drawer/wrappers/contact/tracking';
import moment from 'moment';
import { InMailStatusConstants } from '@copilot/common/constant/inMailConstant';
import isEmpty from 'lodash/isEmpty';
import { DeclinedMessage } from '@copilot/common/copy/inMailCopy';
import { MessageResponse } from '@copilot/data/responses/interface';
import { ContactAvatar } from '@copilot/common/components/componentModels/activities/conversationWrapper/item/contact-avatar';
import { renderMessageContent } from '@copilot/common/components/componentModels/activities/conversationWrapper/item/util';
import styles from './conversationTab.module.less';

type MessageItemProps = ListItemProps & {
    message: MessageResponse;
    sourceName: string;
    targetName: string;
    threadUrl: string | undefined;
    onTracking?: (params: ProspectDrawerTrackingParams) => void;
    isViewedByOwner?: boolean;
};

const { Link, Text } = Typography;

/**
 * Component responsible for rendering a single message in the conversation tab
 * @param param0
 * @returns
 */
function ConversationItemV2({
    sourceName,
    isViewedByOwner,
    threadUrl,
    targetName,
    onTracking,
    message: { timestamp, isSelf, data, subject, pending: isPending, inMailStatus },
}: MessageItemProps) {
    const date = timestamp ? moment(timestamp).format('hh:mmA') : null;
    const messageSender = isSelf ? `${sourceName} ${isViewedByOwner ? '(You)' : ''}` : targetName;
    const isInMailCampaignMessage = !isEmpty(subject);
    const isInMailDeclined = inMailStatus === InMailStatusConstants.Declined;

    return (
        <List.Item
            className={[
                styles.messageItem,
                styles.activityItem,
                isPending ? styles.pendingMessage : '',
                isInMailDeclined ? styles.inMailDeclined : '',
            ].join(' ')}
        >
            <div className={styles.messageContentWrapper}>
                {isSelf ? (
                    <OrgMemberAvatar />
                ) : (
                    <ContactAvatar contactName={targetName} type="message" />
                )}
                <div className={styles.messageItemWrapper}>
                    <div className={styles.itemHeader}>
                        {!isSelf ? (
                            <Link
                                href={threadUrl}
                                target="_blank"
                                underline
                                onClick={() =>
                                    onTracking?.({
                                        buttonClicked: 'View on LinkedIn Button',
                                    })
                                }
                            >
                                <Text strong>{messageSender}</Text>
                            </Link>
                        ) : (
                            <>
                                <Text strong>{messageSender}</Text>
                            </>
                        )}
                        <div>
                            {isPending ? (
                                <>
                                    <span className={styles.messageItemSubtext}>
                                        {' '}
                                        {'Pending,'}{' '}
                                    </span>

                                    <Link href={'/outbox'} className={styles.outBoxLink}>
                                        View in outbox
                                    </Link>
                                    {''}
                                </>
                            ) : (
                                <span className={styles.messageItemSubtext}>{date}</span>
                            )}
                        </div>
                    </div>
                    {isInMailDeclined && <DeclinedMessage />}
                    <div className={styles.itemContent}>{renderMessageContent(data, subject)}</div>
                </div>
            </div>
            {isInMailCampaignMessage && (
                <div className={styles.inMailCampaignFooter}>
                    <Text className={styles.messageItemSubtext}>InMail Campaign</Text>
                </div>
            )}
        </List.Item>
    );
}

function OrgMemberAvatar() {
    return (
        <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M0 4C0 1.79086 1.79086 0 4 0H20C22.2091 0 24 1.79086 24 4V20C24 22.2091 22.2091 24 20 24H4C1.79086 24 0 22.2091 0 20V4Z"
                fill="#FFB801"
            />
            <path
                d="M16.7373 15.4398C16.4794 14.8289 16.1052 14.274 15.6354 13.8061C15.167 13.3367 14.6122 12.9625 14.0016 12.7041C13.9961 12.7014 13.9907 12.7 13.9852 12.6973C14.8369 12.082 15.3907 11.0799 15.3907 9.94922C15.3907 8.07617 13.8731 6.55859 12 6.55859C10.127 6.55859 8.60941 8.07617 8.60941 9.94922C8.60941 11.0799 9.16312 12.082 10.0149 12.6986C10.0094 12.7014 10.0039 12.7027 9.99847 12.7055C9.38597 12.9639 8.83636 13.3344 8.36468 13.8074C7.89534 14.2758 7.52113 14.8306 7.26273 15.4412C7.00887 16.039 6.87196 16.6799 6.85941 17.3293C6.85904 17.3439 6.8616 17.3584 6.86694 17.372C6.87227 17.3856 6.88027 17.398 6.89046 17.4084C6.90065 17.4189 6.91284 17.4272 6.92629 17.4328C6.93974 17.4385 6.95419 17.4414 6.96878 17.4414H7.7891C7.84925 17.4414 7.8971 17.3936 7.89847 17.3348C7.92582 16.2793 8.34964 15.2908 9.09886 14.5416C9.87406 13.7664 10.9035 13.3398 12 13.3398C13.0965 13.3398 14.126 13.7664 14.9012 14.5416C15.6504 15.2908 16.0743 16.2793 16.1016 17.3348C16.103 17.3949 16.1508 17.4414 16.211 17.4414H17.0313C17.0459 17.4414 17.0603 17.4385 17.0738 17.4328C17.0872 17.4272 17.0994 17.4189 17.1096 17.4084C17.1198 17.398 17.1278 17.3856 17.1331 17.372C17.1385 17.3584 17.141 17.3439 17.1407 17.3293C17.127 16.6758 16.9916 16.04 16.7373 15.4398ZM12 12.3008C11.3725 12.3008 10.7819 12.0561 10.3375 11.6117C9.8932 11.1674 9.64847 10.5768 9.64847 9.94922C9.64847 9.32168 9.8932 8.73105 10.3375 8.28672C10.7819 7.84238 11.3725 7.59766 12 7.59766C12.6276 7.59766 13.2182 7.84238 13.6625 8.28672C14.1069 8.73105 14.3516 9.32168 14.3516 9.94922C14.3516 10.5768 14.1069 11.1674 13.6625 11.6117C13.2182 12.0561 12.6276 12.3008 12 12.3008Z"
                fill="black"
                fill-opacity="0.65"
            />
        </svg>
    );
}

export default ConversationItemV2;
